import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Flame, Target, Star } from 'lucide-react';
import { Card } from '../ui/Card';
import { useMotionBudget } from '../../app/hooks/useMotionBudget';
import { WeeklyActivityDots } from '../shared/WeeklyActivityDots';
import type { DayActivity } from '../../utils/activityHelpers';
interface GlanceBarProps {
  streakDays: number;
  dailyProgress: number;
  dailyGoalTarget: number;
  level: number;
  xp: number;
  weeklyGoal: number;
  weeklyActivity: DayActivity[];
  onNavigateToModules?: () => void;
}
export function GlanceBar({
  streakDays,
  dailyProgress,
  dailyGoalTarget,
  level,
  xp,
  weeklyGoal,
  weeklyActivity,
  onNavigateToModules
}: GlanceBarProps) {
  const {
    isReducedMotion
  } = useMotionBudget();
  // Calculate progress percentages
  const streakProgress = Math.min(100, streakDays / 7 * 100);
  const dailyGoalProgress = Math.min(100, dailyProgress / dailyGoalTarget * 100);
  // XP progress within level (simplified: assume 1000 XP per level for display)
  const xpInCurrentLevel = xp % 1000;
  const levelProgress = Math.min(100, xpInCurrentLevel / 1000 * 100);
  const isDailyGoalReached = dailyProgress >= dailyGoalTarget;
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: -10
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      scale: 1
    }
  };
  return <motion.div variants={containerVariants} initial={isReducedMotion ? 'visible' : 'hidden'} animate="visible" className="mb-6" data-id="element-1341">
      <Card className="p-3 sm:p-4 bg-white border-2 border-slate-100 rounded-2xl shadow-sm flex flex-col gap-4 overflow-hidden" data-id="element-1342">
        <div className="flex items-center justify-between gap-2 sm:gap-4" data-id="element-1343">
          {/* Streak Segment */}
          <motion.button variants={itemVariants} className="flex-1 flex flex-col gap-1.5 p-2 rounded-xl hover:bg-slate-50 transition-colors text-left group" data-id="element-1344">
            <div className="flex items-center gap-1.5 sm:gap-2" data-id="element-1345">
              <Flame className={`w-4 h-4 sm:w-5 sm:h-5 ${streakDays > 0 ? 'text-orange-500' : 'text-slate-400'}`} data-id="element-1346" />
              <span className="text-xs sm:text-sm font-bold text-slate-700 truncate" data-id="element-1347">
                {streakDays}/{weeklyGoal} dagen
              </span>
            </div>
            <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden" data-id="element-1348">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${streakProgress}%`
            }} transition={{
              duration: 0.8,
              ease: 'easeOut',
              delay: 0.2
            }} className={`h-full rounded-full ${streakDays > 0 ? 'bg-orange-500' : 'bg-slate-300'}`} data-id="element-1349" />
            </div>
          </motion.button>

          <div className="w-px h-8 bg-slate-100 flex-shrink-0" data-id="element-1350" />

          {/* Daily Goal Segment */}
          <motion.button variants={itemVariants} onClick={onNavigateToModules} className="flex-1 flex flex-col gap-1.5 p-2 rounded-xl hover:bg-slate-50 transition-colors text-left group" data-id="element-1351">
            <div className="flex items-center gap-1.5 sm:gap-2" data-id="element-1352">
              <Target className={`w-4 h-4 sm:w-5 sm:h-5 ${isDailyGoalReached ? 'text-green-500' : dailyProgress > 0 ? 'text-amber-500' : 'text-slate-400'}`} data-id="element-1353" />
              <span className="text-xs sm:text-sm font-bold text-slate-700 truncate" data-id="element-1354">
                {dailyProgress}/{dailyGoalTarget} doel
              </span>
            </div>
            <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden" data-id="element-1355">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${dailyGoalProgress}%`
            }} transition={{
              duration: 0.8,
              ease: 'easeOut',
              delay: 0.3
            }} className={`h-full rounded-full ${isDailyGoalReached ? 'bg-green-500' : 'bg-amber-500'}`} data-id="element-1356" />
            </div>
          </motion.button>

          <div className="w-px h-8 bg-slate-100 flex-shrink-0" data-id="element-1357" />

          {/* Level Segment */}
          <motion.div variants={itemVariants} className="flex-1 flex flex-col gap-1.5 p-2 rounded-xl text-left" data-id="element-1358">
            <div className="flex items-center gap-1.5 sm:gap-2" data-id="element-1359">
              <Star className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" data-id="element-1360" />
              <span className="text-xs sm:text-sm font-bold text-slate-700 truncate" data-id="element-1361">
                Level {level}
              </span>
            </div>
            <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden" data-id="element-1362">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${levelProgress}%`
            }} transition={{
              duration: 0.8,
              ease: 'easeOut',
              delay: 0.4
            }} className="h-full bg-blue-500 rounded-full" data-id="element-1363" />
            </div>
          </motion.div>
        </div>

        {/* Weekly Activity Row */}
        <motion.div variants={itemVariants} className="pt-3 border-t border-slate-100" data-id="element-1364">
          <WeeklyActivityDots weeklyActivity={weeklyActivity} color="emerald" weeklyGoal={weeklyGoal} data-id="element-1365" />
        </motion.div>
      </Card>
    </motion.div>;
}